import {
  Calendar,
  CalendarDataEvent,
  DateCalendarSelector,
  Slot,
} from "@pushpress/shared-components";
import { endOfWeek, startOfWeek } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React, {
  Ref,
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { AppContext } from "../../../context/app";
import { useGetStaffForAvailabilityQuery } from "./ViewAvailability-generated.hooks";
import styles from "./ViewAvailability.module.scss";

type AvailabilityProps = {
  userUuid: string;
};

export type ViewAvailabilityRef = {
  refresh: VoidFunction;
};

const ViewAvailability = (
  props: AvailabilityProps,
  ref: Ref<ViewAvailabilityRef>,
) => {
  const { userUuid } = props;

  const { client } = useContext(AppContext);

  const [date, setDate] = useState(new Date());

  const startDate = startOfWeek(date);

  const endDate = endOfWeek(date);

  const { data, loading, refetch } = useGetStaffForAvailabilityQuery({
    variables: {
      userUuid,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
  });

  useImperativeHandle(
    ref,
    () => {
      return {
        refresh: refetch,
      };
    },
    [refetch],
  );

  const blockedSlots = useMemo<Slot[]>(() => {
    const ISO_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ss'Z'";
    const busyTimes = data?.getStaff[0]?.busyTimes || [];
    const timezone = client.timezone;
    return busyTimes.map((time, index) => ({
      id: String(index),
      start: new Date(
        formatInTimeZone(new Date(time.startTime), timezone, ISO_DATE_FORMAT),
      ),
      end: new Date(
        formatInTimeZone(new Date(time.endTime), timezone, ISO_DATE_FORMAT),
      ),
    }));
  }, [data, client]);

  return (
    <div className={styles.root}>
      <div>
        <DateCalendarSelector date={date} view="week" onChange={setDate} />
      </div>
      <div className="mt-4">
        <Calendar
          date={date}
          view="week"
          events={[]}
          blockedSlots={blockedSlots}
          timezone="UTC"
          hideAllDaySlot
          stickyHeaders
          loading={loading}
          eventAccessor={() => null as unknown as CalendarDataEvent}
        />
      </div>
    </div>
  );
};

export default forwardRef(ViewAvailability);
